import { getFeedbackDisplayNumber } from './feedbackNumbering';

const TITLE_WORD_COUNT = 3;
const FALLBACK_TITLE = 'New idea';

// the text form of a New Idea chip that is sent to the chat backend e.g. #12 (Some title)
const MENTION_TEXT_REGEX = /#(\d+) \(([^()]*)\)/;

function getDetailedFeedbackText(item) {
  const raw = item?.detailed_feedback;

  if (Array.isArray(raw)) {
    return raw.join(' ');
  }

  return typeof raw === 'string' ? raw : '';
}

function isNewIdea(item) {
  return Boolean(
    item?.isDeeperInsight
    || item?.category === 'deeper-insights'
    || item?.category === 'deeper_insight'
  );
}

// the first three words of detailed_feedback will be the title, always ending with a letter
export function getNewIdeaTitle(item) {
  const title = getDetailedFeedbackText(item)
    .replace(/[()[\]{}*_`#~"“”]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, TITLE_WORD_COUNT)
    .join(' ')
    .replace(/[^a-zA-Z]+$/, '');

  return title || FALLBACK_TITLE;
}

// Builds the options shown in the "#" dropdown from the audit's feedback items.
export function getNewIdeaMentionOptions({ feedback, displayNumberMap }) {
  if (!Array.isArray(feedback)) {
    return [];
  }

  return feedback
    .filter(item => isNewIdea(item) && item.uuid != null)
    .map(item => ({
      insightUuid: String(item.uuid),
      feedbackId: getFeedbackDisplayNumber(item, displayNumberMap, item.index),
      title: getNewIdeaTitle(item),
      description: getDetailedFeedbackText(item).trim(),
    }))
    .sort((a, b) => a.feedbackId - b.feedbackId);
}

export function filterMentionOptions(options, query) {
  const normalizedQuery = (query || '').trim().toLowerCase();

  if (!normalizedQuery) {
    return options;
  }

  return options.filter(option => (
    String(option.feedbackId).indexOf(normalizedQuery) === 0
    || option.title.toLowerCase().includes(normalizedQuery)
  ));
}

export function getMentionLabel(option) {
  return `#${option.feedbackId} ${option.title}`;
}

// defines how a chip is written into the chat message
export function serializeMention(option) {
  return `#${option.feedbackId} (${option.title})`;
}

/**
 * Splits chat text into text / mention segments. Only the first "#id (title)" that matches a known
 * New Idea becomes a mention (one chip per message); anything else stays plain text.
 */
export function parseMentionText(text, options) {
  const source = typeof text === 'string' ? text : '';
  const optionsById = new Map((options || []).map(option => [String(option.feedbackId), option]));
  const expression = new RegExp(MENTION_TEXT_REGEX.source, 'g');
  let match = expression.exec(source);

  while (match) {
    const option = optionsById.get(match[1]);

    if (option) {
      const segments = [];
      const before = source.slice(0, match.index);
      const after = source.slice(match.index + match[0].length);

      if (before) {
        segments.push({ type: 'text', value: before });
      }

      segments.push({ type: 'mention', option });

      if (after) {
        segments.push({ type: 'text', value: after });
      }

      return segments;
    }

    match = expression.exec(source);
  }

  return source ? [{ type: 'text', value: source }] : [];
}

// Typed SSE events the Ask Audit chat endpoint (/critique_chat) can send for prototype generation
export const CHAT_EVENT_TYPES = {
  PROTOTYPE_JOB_STARTED: 'prototype_job_started',
  PROTOTYPE_JOB_STATUS: 'prototype_job_status',
  REFINEMENT_OPTIONS: 'refinement_options',
  PROTOTYPE_ERROR: 'prototype_error',
};

export const PROTOTYPE_OPERATIONS = {
  GENERATE: 'generate',
  REFINE: 'refine',
};

const PROTOTYPE_EVENT_TYPES = Object.values(CHAT_EVENT_TYPES);

export const isPrototypeEvent = event => PROTOTYPE_EVENT_TYPES.includes(event?.type);

// One chat message from the options picked for a variant
export function buildRefinementMessage(labels, customText) {
  const changes = [...labels, customText]
    .map(change => (change || '').trim())
    .filter(Boolean)
    .map(change => (/[.!?]$/.test(change) ? change : `${change}.`));

  return changes.length > 0 ? `Please make these changes: ${changes.join(' ')}` : '';
}
